






































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { shuffle } from "lodash";
import { SwiperOptions } from "swiper";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import { Banner, BannerSubjectTypeEnum } from "@scrile/api-provider/dist/api/BannersProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import { isVideoFile } from "@/lib/fileHelpers";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import LandingSliderItem from "../LandingSliderItem";
import { SliderItem } from "../LandingSliderItem/LandingSliderItem.vue";

export default defineComponent({
  name: "LandingSlider",
  components: { LandingSliderItem, BaseSpinner, BaseIcon, Swiper, SwiperSlide },
  props: {
    type: { type: String as () => BannerSubjectTypeEnum, default: BannerSubjectTypeEnum.LANDING_PAGE },
    tagId: { type: String },
    miniView: { type: Boolean, default: false },
    fetchPerformers: { type: Boolean, default: true },
    internalLinkMode: { type: Boolean, default: false },
  },
  setup(props) {
    const randId = Math.random()
      .toString(36)
      .substr(2, 9);
    const loading = ref(true);
    const swiperRef = ref();
    const activeIndex = ref(1);
    const performers = ref<UserPublicData[]>([]);
    const banners = ref<Banner[]>([]);

    const spliceArrays = (
      first: Array<UserPublicData | Banner>,
      second: Array<UserPublicData | Banner>,
      firstRole: keyof SliderItem,
      secondRole: keyof SliderItem
    ) => {
      let result: SliderItem[] = [];
      const frequency = Math.floor(first.length / second.length);
      first.map((el, i) => {
        i++;
        result.push({ [firstRole]: el });
        if (second.length && !(i % frequency)) result.push({ [secondRole]: second.shift() });
      });
      return result;
    };

    const items = computed<SliderItem[]>(() => {
      const isMoreBanners = banners.value.length > performers.value.length;
      return spliceArrays(
        isMoreBanners ? [...banners.value] : [...performers.value],
        !isMoreBanners ? [...banners.value] : [...performers.value],
        isMoreBanners ? "banner" : "performer",
        !isMoreBanners ? "banner" : "performer"
      );
    });

    const randIdClass = computed(() => `__id-${randId}`);

    const swiperOptions: SwiperOptions = {
      ...(!props.miniView
        ? {
            slidesPerView: 1,
          }
        : {
            spaceBetween: 16,
            slidesPerView: 2,
            breakpoints: {
              769: {
                slidesPerView: 3,
              },
            },
          }),
      loop: true,
      autoplay: {
        delay: 4000,
        disableOnInteraction: false,
        waitForTransition: false,
      },
      pagination: {
        el: `.${randIdClass.value}.landing-slider__pagination`,
        bulletClass: "landing-slider__page",
        bulletActiveClass: "__active",
      },
      navigation: {
        prevEl: `.${randIdClass.value}.landing-slider__prev`,
        nextEl: `.${randIdClass.value}.landing-slider__next`,
      },
      on: {
        slideChange: () => {
          activeIndex.value = swiperRef.value?.$swiper.realIndex ?? 0;
        },
      },
    };

    const autoplayDelay = computed(() =>
      items.value.map(i => (i.performer?.background && isVideoFile(i.performer.background) ? 8000 : 4000))
    );

    const callbackBanners = async (tagId: string = "0") => {
      return shuffle(
        await providers.BannersProvider.findAll({
          data: { subject: { subjectId: "0", subjectType: props.type }, tagId },
        })
      );
    };

    const getBanners = async () => {
      const bannersResponse = await callbackBanners(props.tagId);
      if (bannersResponse.length > 0 || props.type === BannerSubjectTypeEnum.SEARCH_MINI_SLIDER) {
        banners.value = bannersResponse;
      } else {
        banners.value = await callbackBanners();
      }
    };
    const getData = async () => {
      try {
        loading.value = true;
        if (props.fetchPerformers) {
          performers.value = (
            await providers.PerformerListProvider.performerListSearch({
              data: { size: 10, featuredModel: true, hideFromHomePage: false, sort: "RANDOM" },
            })
          ).result;
        }
        await getBanners();
      } finally {
        loading.value = false;
      }
    };

    watch(toRef(props, "tagId"), getBanners);
    getData();

    return { loading, items, swiperOptions, autoplayDelay, swiperRef, activeIndex, banners, performers, randIdClass };
  },
});
